<script setup lang="ts">
const { open: openIntentModal } = useIntentModal();

interface ProblemCard {
  icon: string;
  title: string;
  description: string;
  buttonText: string;
  targetId: string;
  intent?: string; // If set, opens intent modal instead of scrolling
}

const problems: ProblemCard[] = [
  {
    icon: "lucide:wifi-off",
    title: "Unreliable Connectivity",
    description:
      "Internet and Wi-Fi keep dropping, hurting productivity and frustrating your team.",
   buttonText: "See Connectivity solutions",
    targetId: "connectivity",
  },
  {
    icon: "lucide:shield-off",
    title: "Outgrown Security",
    description:
      "You've outgrown your current security system and worry about gaps and blind spots.",
   buttonText: "See Security solutions",
    targetId: "security",
  },
  {
    icon: "lucide:puzzle",
    title: "Too Many Vendors",
    description:
      "Tired of juggling multiple providers with no single accountable partner.",
    buttonText: "Talk to one partner",
    targetId: "itcloud",
    intent: "consultation",
  },
];

const handleCardClick = (problem: ProblemCard) => {
  if (problem.intent) {
    openIntentModal(problem.intent);
  } else {
    const element = document.getElementById(problem.targetId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<template>
  <section class="bg-neutral-50 py-16 lg:py-20">
    <div class="mx-auto max-w-7xl px-8">
      <div class="text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Does this sound familiar?
        </h2>
        <p class="mt-4 text-xl text-slate-600">
          Pick your biggest challenge - we solve these every day.
        </p>
      </div>

      <div class="mt-12 grid gap-8 md:grid-cols-3">
        <button
          v-for="problem in problems"
          :key="problem.targetId"
          type="button"
          class="group relative flex min-h-80 flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white p-10 text-left shadow-xs transition-all duration-300 hover:-translate-y-1 focus-visible:-translate-y-1 focus-visible:outline-none focus-visible:ring-primary-400 focus-visible:ring-offset-2"
          @click="handleCardClick(problem)"
        >
          <!-- Icon -->
          <div class="mb-6">
            <Icon
              :name="problem.icon"
              size="48"
              class="text-mw-accent-500 transition-transform duration-300 group-hover:scale-110"
            />
          </div>

          <!-- Title -->
          <h3 class="text-2xl font-extrabold text-dark">
            {{ problem.title }}
          </h3>

          <!-- Description -->
          <p class="mt-4 flex-1 text-lg leading-7 text-neutral-600">
            {{ problem.description }}
          </p>

          <!-- CTA pill button -->
          <div class="mt-10">
            <div
              class="inline-flex items-center gap-2 rounded-full border border-primary-400 px-5 py-2.5 text-base font-bold text-primary-400 transition-all"
            >
              {{ problem.buttonText }}
              <Icon
                name="lucide:arrow-right"
                size="20"
                class="transition-transform group-hover:translate-x-0.5"
              />
            </div>
          </div>

          <!-- Sliding accent line -->
          <div
            class="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 rounded-b-2xl bg-primary-400 transition-transform duration-300 group-hover:scale-x-100 group-focus-visible:scale-x-100"
            aria-hidden="true"
          ></div>
        </button>
      </div>
    </div>
  </section>
</template>
